const buildDatasetDataApiScript = (insightsUrl: string) => `
      const createDatasetDataApi = ({ auth, logMessage }) => {
        const normalize = (value) => String(value || '').trim().toLowerCase();

        const unwrapEntity = (entity) => (entity && entity.entity ? entity.entity : entity);

        const getEntityTypeId = (entity) => {
          if (!entity || typeof entity !== 'object') return undefined;
          return (
            entity.typeId ||
            entity.type_id ||
            (entity.type && (entity.type.id || entity.type.value)) ||
            entity.entityTypeId ||
            entity.entity_type_id
          );
        };

        const findFieldByName = (entityType, fieldName) => {
          const fields = (entityType && entityType.fields) || [];
          const normalized = normalize(fieldName);
          return fields.find((field) => normalize(field.name) === normalized);
        };

        const readFieldValue = (entity, fieldId) => {
          const values = entity && typeof entity === 'object' ? entity.valuesByFieldId : undefined;
          if (!values || typeof values !== 'object') return '';
          const raw = values[fieldId];
          if (raw === null || raw === undefined) return '';
          if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
          if (typeof raw === 'object') {
            const value = raw.value;
            if (value === null || value === undefined) return '';
            return String(value);
          }
          return '';
        };

        const normalizeParamType = (rawType) => {
          const type = typeof rawType === 'string' ? rawType.trim().toLowerCase() : '';
          return type === 'integer' ? 'integer' : 'string';
        };

        const normalizeDefaultValue = (rawValue) => {
          if (rawValue === null || rawValue === undefined) return null;
          if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim();
            if (!trimmed) return '';
            const lower = trimmed.toLowerCase();
            if (lower === 'none' || lower === 'null') return null;
            return rawValue;
          }
          if (Array.isArray(rawValue)) {
            return rawValue
              .filter((item) => item !== null && item !== undefined)
              .map((item) => String(item));
          }
          return String(rawValue);
        };

        const normalizeParamTarget = (rawTarget, fallbackName) => {
          if (rawTarget === null) return null;
          if (rawTarget === undefined) return fallbackName;
          if (typeof rawTarget !== 'string') return fallbackName;
          const trimmed = rawTarget.trim();
          return trimmed.length > 0 ? trimmed : fallbackName;
        };

        const parseParams = (raw) => {
          if (!Array.isArray(raw)) return [];
          const params = [];
          raw.forEach((entry) => {
            if (!entry || typeof entry !== 'object') return;
            const record = entry;
            const name = typeof record.name === 'string' ? record.name.trim() : '';
            if (!name) return;
            const hasDefault = 'default' in record || 'defaultValue' in record;
            const rawDefault = hasDefault ? ('default' in record ? record.default : record.defaultValue) : null;
            const defaultValue = normalizeDefaultValue(rawDefault);
            const type = normalizeParamType(record.type);
            const target = normalizeParamTarget(record.target, name);
            params.push({ name, defaultValue, type, target });
          });
          return params;
        };

        const parseDatasetRecord = (parsed) => {
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return { error: 'Dataset payload must be a YAML object.' };
          }
          const query = parsed.query ? String(parsed.query).trim() : '';
          if (!query) return { error: 'Dataset payload must include a "query" field.' };
          const params = parseParams(parsed.params);
          return { query, params };
        };

        const parseDatasetPayload = (raw) => {
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            return parseDatasetRecord(raw);
          }
          if (!raw || !String(raw).trim()) return { error: 'Dataset payload is empty.' };
          if (!window.YAML || typeof window.YAML.parse !== 'function') {
            return { error: 'YAML parser is unavailable.' };
          }
          try {
            const parsed = window.YAML.parse(raw);
            return parseDatasetRecord(parsed);
          } catch (error) {
            const message = error && error.message ? error.message : String(error);
            return { error: 'Failed to parse dataset YAML: ' + message };
          }
        };

        const buildParamMap = (params) => {
          const map = {};
          if (!Array.isArray(params)) return map;
          params.forEach((param) => {
            if (!param || typeof param !== 'object') return;
            const name = String(param.name || '').trim();
            if (!name) return;
            const type = param.type === 'integer' ? 'integer' : 'string';
            const hasExplicitNullTarget =
              Object.prototype.hasOwnProperty.call(param, 'target') && param.target === null;
            const target = hasExplicitNullTarget
              ? null
              : typeof param.target === 'string' && param.target.trim().length > 0
                ? param.target.trim()
                : name;
            const rawDefault = Object.prototype.hasOwnProperty.call(param, 'defaultValue') ? param.defaultValue : null;
            let defaultValue = null;
            if (rawDefault !== null && rawDefault !== undefined) {
              if (Array.isArray(rawDefault)) {
                defaultValue = rawDefault
                  .filter((item) => item !== null && item !== undefined)
                  .map((item) => String(item));
              } else {
                defaultValue = String(rawDefault);
              }
            }
            map[name] = { type, defaultValue, target };
          });
          return map;
        };

        const normalizeProvidedValues = (provided) => {
          if (!provided || typeof provided !== 'object') return {};
          const result = {};
          Object.keys(provided).forEach((key) => {
            const value = provided[key];
            if (value === null || value === undefined) return;
            if (Array.isArray(value)) {
              result[key] = value
                .filter((item) => item !== null && item !== undefined)
                .map((item) => String(item));
            } else {
              result[key] = String(value);
            }
          });
          return result;
        };

        const normalizeValueList = (rawValue) => {
          if (Array.isArray(rawValue)) {
            return rawValue
              .filter((item) => item !== null && item !== undefined)
              .map((item) => String(item).trim())
              .filter((item) => item.length > 0);
          }
          if (rawValue === null || rawValue === undefined) return [];
          const value = String(rawValue).trim();
          return value.length > 0 ? [value] : [];
        };

        const escapeSqlString = (value) => String(value).replace(/'/g, "''");

        const formatParamValues = (paramKey, paramDef, rawValue) => {
          const type = paramDef && paramDef.type === 'integer' ? 'integer' : 'string';
          const values = normalizeValueList(rawValue);
          if (values.length === 0) return [];
          if (type === 'integer') {
            const valid = [];
            values.forEach((value) => {
              if (/^-?\\d+$/.test(value)) {
                valid.push(value);
              } else if (typeof logMessage === 'function') {
                logMessage('Dataset param "' + paramKey + '" expects an integer; dropped value "' + value + '".');
              }
            });
            return valid;
          }
          return values.map((value) => "'" + escapeSqlString(value) + "'");
        };

        const formatParamReplacement = (paramKey, paramDef, rawValue) => {
          const target = paramDef && typeof paramDef.target === 'string' ? paramDef.target : null;
          const hasTarget = Boolean(target);
          if (rawValue === null || rawValue === undefined) {
            return hasTarget ? '1=1' : '';
          }
          const isArrayValue = Array.isArray(rawValue);
          const formattedValues = formatParamValues(paramKey, paramDef, rawValue);
          if (formattedValues.length === 0) {
            return hasTarget ? (isArrayValue ? '1=1' : target + " = ''") : '';
          }
          if (!hasTarget) {
            return isArrayValue ? formattedValues.join(', ') : formattedValues[0];
          }
          return isArrayValue
            ? target + ' in (' + formattedValues.join(', ') + ')'
            : target + ' = ' + formattedValues[0];
        };

        const applyParams = (template, values, paramMap) =>
          String(template || '').replace(/\\{\\{\\s*([^}]+?)\\s*\\}\\}/g, (_match, rawKey) => {
            const paramKey = String(rawKey || '').trim();
            if (!paramKey) return '';
            const hasParamDef = paramMap && Object.prototype.hasOwnProperty.call(paramMap, paramKey);
            if (!hasParamDef) {
              if (typeof logMessage === 'function') {
                logMessage('Dataset param "' + paramKey + '" is not defined in params.');
              }
              return '';
            }
            const hasValue = values && Object.prototype.hasOwnProperty.call(values, paramKey);
            const paramDef = paramMap[paramKey];
            const rawValue = hasValue ? values[paramKey] : paramDef.defaultValue;
            return formatParamReplacement(paramKey, paramDef, rawValue);
          });

        const logResolvedQuery = (label, query) => {
          if (typeof logMessage !== 'function') return;
          logMessage(String(label || 'Running dataset query:'));
          String(query || '')
            .split(/\\r?\\n/)
            .forEach((line) => {
              logMessage('> ' + line);
            });
        };

        const requireAuthForEntities = () => {
          if (!auth || !auth.accessToken || !auth.organizationId || !auth.entityApiUrl) {
            throw new Error('Missing authentication context');
          }
        };

        const fetchJson = async (url) => {
          if (!auth || !auth.accessToken) throw new Error('Missing authentication context');
          const response = await fetch(url, {
            headers: {
              Authorization: 'Bearer ' + auth.accessToken,
              Accept: 'application/json',
            },
          });
          if (!response.ok) throw new Error('HTTP ' + response.status + ' ' + response.statusText);
          return response.json();
        };

        const getEntity = async (id) => {
          requireAuthForEntities();
          const url = auth.entityApiUrl + '/organizations/' + auth.organizationId + '/entities/' + encodeURIComponent(id);
          return fetchJson(url);
        };

        const getEntityType = async (id) => {
          requireAuthForEntities();
          const url = auth.entityApiUrl + '/organizations/' + auth.organizationId + '/types/' + encodeURIComponent(id);
          return fetchJson(url);
        };

        const pad2 = (value) => String(value).padStart(2, '0');

        const formatDateUtc = (dateValue) =>
          String(dateValue.getUTCFullYear()) +
          '-' +
          pad2(dateValue.getUTCMonth() + 1) +
          '-' +
          pad2(dateValue.getUTCDate());

        const formatTimestampUtc = (dateValue) =>
          formatDateUtc(dateValue) +
          ' ' +
          pad2(dateValue.getUTCHours()) +
          ':' +
          pad2(dateValue.getUTCMinutes()) +
          ':' +
          pad2(dateValue.getUTCSeconds());

        const toDateFromMicroseconds = (micros) => {
          if (typeof micros === 'bigint') return new Date(Number(micros / 1000n));
          return new Date(Number(micros) / 1000);
        };

        const toDateFromNanoseconds = (nanos) => {
          if (typeof nanos === 'bigint') return new Date(Number(nanos / 1000000n));
          return new Date(Number(nanos) / 1000000);
        };

        const parquetParsers = {
          dateFromDays: (days) => formatDateUtc(new Date(Number(days) * 86400000)),
          timestampFromMilliseconds: (millis) => formatTimestampUtc(new Date(Number(millis))),
          timestampFromMicroseconds: (micros) => formatTimestampUtc(toDateFromMicroseconds(micros)),
          timestampFromNanoseconds: (nanos) => formatTimestampUtc(toDateFromNanoseconds(nanos)),
        };

        const MAX_SAFE_INTEGER_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);
        const MIN_SAFE_INTEGER_BIGINT = BigInt(Number.MIN_SAFE_INTEGER);

        const normalizeQueryValue = (value) => {
          if (typeof value === 'bigint') {
            if (value <= MAX_SAFE_INTEGER_BIGINT && value >= MIN_SAFE_INTEGER_BIGINT) {
              return Number(value);
            }
            return value.toString();
          }
          if (value instanceof Date) {
            return value.toISOString();
          }
          if (Array.isArray(value)) {
            return value.map((entry) => normalizeQueryValue(entry));
          }
          return value;
        };

        const normalizeQueryRows = (rows) =>
          (Array.isArray(rows) ? rows : []).map((row) => {
            if (!row || typeof row !== 'object' || Array.isArray(row)) {
              return row;
            }
            const normalized = {};
            Object.keys(row).forEach((key) => {
              normalized[key] = normalizeQueryValue(row[key]);
            });
            return normalized;
          });

        const runInsightsQuery = async (query) => {
          if (!auth || !auth.accessToken) throw new Error('Missing authentication context');
          const { parquetReadObjects, parquetMetadataAsync, parquetSchema } =
            await import('https://cdn.jsdelivr.net/npm/hyparquet@1/+esm');

          const startTime = performance.now();
          try {
            const submitRes = await fetch(${JSON.stringify(insightsUrl)} + '/redshift-query', {
              method: 'POST',
              headers: {
                'Content-Type': 'text/plain',
                Authorization: 'Bearer ' + auth.accessToken,
              },
              body: query,
            });
            if (submitRes.status !== 202) throw new Error('Submit failed: ' + await submitRes.text());

            const statusPath = submitRes.headers.get('Location');
            if (!statusPath) throw new Error('No Location header');

            let status;
            const pollEnd = Date.now() + 120000;
            while (Date.now() < pollEnd) {
              const statusRes = await fetch(new URL(statusPath, ${JSON.stringify(insightsUrl)}).href, {
                headers: { Authorization: 'Bearer ' + auth.accessToken },
              });
              status = await statusRes.json();

              if (status.type === 'JobStatusFinished') break;
              if (status.type === 'JobStatusFailed') throw new Error(status.errorMessage || 'Query failed');
              if (status.type === 'JobStatusResultExpired') throw new Error('Results expired');

              await new Promise((resolve) => setTimeout(resolve, 500));
            }
            if (!status || status.type !== 'JobStatusFinished') throw new Error('Timeout');

            const resultRes = await fetch(new URL(status.resultLocation, ${JSON.stringify(insightsUrl)}).href, {
              headers: { Authorization: 'Bearer ' + auth.accessToken },
            });
            const buffer = await resultRes.arrayBuffer();
            const file = { byteLength: buffer.byteLength, slice: (s, e) => buffer.slice(s, e) };

            const metadata = await parquetMetadataAsync(file);
            const schema = parquetSchema(metadata);
            const rows = normalizeQueryRows(await parquetReadObjects({ file, parsers: parquetParsers }));

            const result = {
              rows,
              fields: schema.children.map((column) => ({ name: column.element.name })),
              rowCount: rows.length,
              durationMs: Math.round(performance.now() - startTime),
            };

            if (typeof logMessage === 'function') {
              logMessage('Query finished: rows=' + result.rowCount + ', durationMs=' + result.durationMs);
            }

            return result;
          } catch (error) {
            if (typeof logMessage === 'function') {
              const message = error && error.message ? error.message : String(error);
              logMessage('Query failed: ' + message);
            }
            throw error;
          }
        };

        const resolveDatasetPayload = async (datasetId) => {
          const normalizedDatasetId = String(datasetId || '').trim();
          if (!normalizedDatasetId) throw new Error('Dataset id is missing.');
          const datasetRaw = await getEntity(normalizedDatasetId);
          const dataset = unwrapEntity(datasetRaw);
          const datasetTypeId = getEntityTypeId(dataset);
          if (!datasetTypeId) throw new Error('Dataset type missing.');
          const datasetType = await getEntityType(datasetTypeId);
          const payloadField = findFieldByName(datasetType, 'Payload');
          if (!payloadField) throw new Error('Dataset payload field missing.');
          const payload = readFieldValue(dataset, payloadField.id).trim();
          if (!payload) throw new Error('Dataset payload is empty.');
          return payload;
        };

        const resolveDatasetQuery = ({ payload, providedValues } = {}) => {
          const parsedQuery = parseDatasetPayload(payload);
          if (parsedQuery.error) throw new Error(parsedQuery.error);
          const paramMap = buildParamMap(parsedQuery.params);
          const resolvedQuery = applyParams(
            parsedQuery.query,
            normalizeProvidedValues(providedValues),
            paramMap
          );
          if (!resolvedQuery) throw new Error('Dataset query is empty.');
          return resolvedQuery;
        };

        const loadDatasetRows = async ({ payload, providedValues } = {}) => {
          const resolvedQuery = resolveDatasetQuery({ payload, providedValues });
          if (!resolvedQuery) throw new Error('Dataset query is empty.');
          logResolvedQuery('Running dataset query:', resolvedQuery);
          return runInsightsQuery(resolvedQuery);
        };

        return {
          loadDatasetRows,
          resolveDatasetPayload,
          normalizeProvidedValues,
        };
      };
`

export default buildDatasetDataApiScript
